/**
 * ItemDetailModal component - displays full item details in a modal.
 * Shows large image, description, all stats, quality info, and character assignment.
 * Styled with parchment/fantasy theme.
 */

import { useState } from 'react';
import { router } from '@inertiajs/react';
import axios from 'axios';
import type { Item, ItemStat } from '@/types/items';

/** Simple character info for assignment dropdown */
export interface AssignableCharacter {
    id: number;
    name: string;
    class_label: string;
}

/** Resource data structure */
interface ResourceData {
    tea: number;
    wood: number;
    mystical: number;
    medicinal: number;
    edible: number;
}

/** Recycle results returned from the API */
interface RecycleResults {
    itemName: string;
    earned: ResourceData;
    before: ResourceData;
    after: ResourceData;
}

interface ItemDetailModalProps {
    /** The item to display */
    item: Item;
    /** Available characters for assignment */
    characters: AssignableCharacter[];
    /** Callback to close the modal */
    onClose: () => void;
    /** Whether to show the unequip button (for equipped items) */
    showUnequip?: boolean;
    /** Whether to show the recycle button (for unequipped, non-unique items) */
    showRecycle?: boolean;
}

/**
 * Get the color for a rarity value (for parchment theme).
 */
function getRarityColor(rarity: string): string {
    switch (rarity) {
        case 'mundane': return '#6b7280';
        case 'common': return '#065f46';
        case 'uncommon': return '#1d4ed8';
        case 'rare': return '#7c3aed';
        case 'epic': return '#b45309';
        case 'legendary': return '#dc2626';
        case 'artifact': return '#ca8a04';
        case 'unique': return '#0891b2';
        default: return '#065f46';
    }
}

/**
 * Displays full item details in a modal overlay with parchment styling.
 */
export function ItemDetailModal({ item, characters, onClose, showUnequip = false, showRecycle = false }: ItemDetailModalProps) {
    const [selectedCharacterId, setSelectedCharacterId] = useState<number | ''>('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [isUnequipping, setIsUnequipping] = useState(false);
    const [isRecycling, setIsRecycling] = useState(false);
    const [showRecycleConfirm, setShowRecycleConfirm] = useState(false);
    const [recycleResults, setRecycleResults] = useState<RecycleResults | null>(null);
    const [recycleError, setRecycleError] = useState<string | null>(null);
    const [assignError, setAssignError] = useState<string | null>(null);

    const stats = Object.entries(item.stats) as [string, ItemStat][];
    const rarityColor = getRarityColor(item.rarity.value);

    // Determine if item can be recycled (not equipped and not Unique rarity)
    const canRecycle = showRecycle && !item.is_equipped && item.rarity.value !== 'unique';

    /**
     * Handle assigning the item to a character.
     */
    const handleAssign = () => {
        if (!selectedCharacterId) return;

        setIsAssigning(true);
        setAssignError(null);
        router.patch(`/items/${item.id}/equip`, {
            character_id: selectedCharacterId,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                onClose();
            },
            onError: (errors) => {
                setIsAssigning(false);
                if (errors.character_id) {
                    setAssignError(errors.character_id);
                } else {
                    setAssignError('Failed to assign item. Please try again.');
                }
            },
        });
    };

    /**
     * Handle unequipping the item from the character.
     */
    const handleUnequip = () => {
        setIsUnequipping(true);
        router.patch(`/items/${item.id}/unequip`, {}, {
            preserveScroll: true,
            onSuccess: () => {
                onClose();
            },
            onError: () => {
                setIsUnequipping(false);
            },
        });
    };

    /**
     * Handle recycling the item for resources.
     */
    const handleRecycle = async () => {
        setIsRecycling(true);
        setRecycleError(null);

        try {
            const response = await axios.patch(`/items/${item.id}/recycle`);
            const data = response.data;

            if (data.success) {
                setRecycleResults({
                    itemName: data.itemName,
                    earned: data.earned,
                    before: data.before,
                    after: data.after,
                });
            } else {
                setRecycleError(data.error || 'Failed to recycle item.');
                setShowRecycleConfirm(false);
            }
        } catch (error: unknown) {
            const axiosError = error as { response?: { data?: { error?: string } } };
            setRecycleError(axiosError.response?.data?.error || 'Failed to recycle item.');
            setShowRecycleConfirm(false);
        } finally {
            setIsRecycling(false);
        }
    };

    /**
     * Handle closing the modal after viewing recycle results.
     */
    const handleCloseAfterRecycle = () => {
        router.reload({ preserveScroll: true });
        onClose();
    };

    // Resource labels for display
    const resourceLabels: Record<keyof ResourceData, string> = {
        tea: 'Tea',
        wood: 'Wood',
        mystical: 'Mystical',
        medicinal: 'Medicinal',
        edible: 'Edible',
    };

    // Parchment modal styles
    const parchmentStyle = {
        background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
        boxShadow: '4px 4px 12px rgba(0,0,0,0.4), -1px -1px 4px rgba(0,0,0,0.1)',
        border: '1px solid #c9b896',
    };

    // If we have recycle results, show the results view
    if (recycleResults) {
        const earnedResources = (Object.entries(recycleResults.earned) as [keyof ResourceData, number][])
            .filter(([, value]) => value > 0);

        return (
            <div
                className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
                onClick={handleCloseAfterRecycle}
            >
                <div
                    className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg"
                    style={parchmentStyle}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Aged edge effect */}
                    <div
                        className="h-3 rounded-t-lg pointer-events-none"
                        style={{ background: 'linear-gradient(180deg, rgba(139,115,85,0.15) 0%, transparent 100%)' }}
                    />

                    {/* Header */}
                    <div className="px-6 py-4 border-b border-amber-900/20">
                        <h2 className="text-xl font-serif font-bold text-green-800 flex items-center gap-2">
                            <span>🎉</span> Item Recycled!
                        </h2>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        <div className="text-center">
                            <p className="font-serif text-amber-900">
                                You recycled <span className="font-bold">"{recycleResults.itemName}"</span> and received:
                            </p>
                        </div>

                        {/* Resources earned */}
                        <div className="bg-amber-900/10 rounded-lg p-4 border border-amber-900/20">
                            <div className="flex flex-wrap justify-center gap-4">
                                {earnedResources.map(([resource, amount]) => (
                                    <div key={resource} className="flex items-center gap-2 bg-green-800/20 border border-green-800/30 rounded-lg px-4 py-2">
                                        <span className="text-green-800 font-serif font-bold text-lg">+{amount}</span>
                                        <span className="font-serif text-amber-900">{resourceLabels[resource]}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Resource comparison table */}
                        <div>
                            <h3 className="text-sm font-serif font-semibold text-amber-800 mb-3">Your Resources</h3>
                            <div className="bg-white/30 rounded-lg overflow-hidden border border-amber-900/20">
                                <table className="w-full text-sm font-serif">
                                    <thead>
                                        <tr className="border-b border-amber-900/20 bg-amber-900/5">
                                            <th className="text-left px-4 py-2 text-amber-800 font-semibold">Resource</th>
                                            <th className="text-right px-4 py-2 text-amber-800 font-semibold">Before</th>
                                            <th className="text-right px-4 py-2 text-amber-800 font-semibold">After</th>
                                            <th className="text-right px-4 py-2 text-amber-800 font-semibold">Change</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(Object.keys(resourceLabels) as (keyof ResourceData)[]).map((resource) => {
                                            const before = recycleResults.before[resource];
                                            const after = recycleResults.after[resource];
                                            const change = after - before;
                                            if (change === 0) return null;
                                            return (
                                                <tr key={resource} className="border-b border-amber-900/10 last:border-0">
                                                    <td className="px-4 py-2 text-amber-900">{resourceLabels[resource]}</td>
                                                    <td className="px-4 py-2 text-right text-amber-800/60">{before}</td>
                                                    <td className="px-4 py-2 text-right text-amber-900 font-semibold">{after}</td>
                                                    <td className="px-4 py-2 text-right text-green-700 font-semibold">+{change}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Close button */}
                        <div className="flex justify-center pt-2">
                            <button
                                type="button"
                                onClick={handleCloseAfterRecycle}
                                className="px-6 py-2 font-serif font-semibold rounded transition-all hover:scale-105"
                                style={{
                                    background: 'linear-gradient(135deg, #166534 0%, #15803d 100%)',
                                    color: '#fff',
                                    border: '1px solid #14532d',
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg"
                style={parchmentStyle}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Aged edge effect */}
                <div
                    className="h-3 rounded-t-lg pointer-events-none"
                    style={{ background: 'linear-gradient(180deg, rgba(139,115,85,0.15) 0%, transparent 100%)' }}
                />

                {/* Header with close button */}
                <div className="sticky top-0 px-6 py-4 flex items-center justify-between border-b border-amber-900/20" style={{ background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 100%)' }}>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-serif font-bold" style={{ color: rarityColor }}>
                                {item.name}
                            </h2>
                            {item.is_blueprint && (
                                <span className="px-2 py-0.5 bg-cyan-700 text-white text-xs font-serif font-medium rounded">
                                    Blueprint
                                </span>
                            )}
                        </div>
                        <p className="text-sm font-serif text-amber-800/70">
                            {item.rarity.label} {item.quality_stars}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-amber-800/50 hover:text-amber-900 transition-colors p-2"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Blueprint Info Banner */}
                    {item.is_blueprint && (
                        <div className="bg-cyan-800/10 border border-cyan-700/30 rounded-lg p-4">
                            <p className="font-serif text-cyan-900 text-sm">
                                <span className="font-semibold">This is a Blueprint.</span> Use it to learn how to craft this item. Once learned, you can craft it yourself if you have the required materials and building.
                            </p>
                        </div>
                    )}

                    {/* Image */}
                    <div className="flex justify-center">
                        {item.image.large ? (
                            <div
                                className="p-4 rounded-lg"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(139,115,85,0.1) 0%, rgba(139,115,85,0.05) 100%)',
                                    border: '1px solid rgba(139,115,85,0.2)',
                                }}
                            >
                                <img
                                    src={item.image.large}
                                    alt={item.name}
                                    className="max-w-full max-h-64 object-contain rounded"
                                />
                            </div>
                        ) : (
                            <div
                                className="w-48 h-48 rounded-lg flex items-center justify-center"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(139,115,85,0.1) 0%, rgba(139,115,85,0.05) 100%)',
                                    border: '1px solid rgba(139,115,85,0.2)',
                                }}
                            >
                                <svg className="w-16 h-16 text-amber-800/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {item.description && (
                        <div>
                            <h3 className="text-sm font-serif font-semibold text-amber-800 mb-2">Description</h3>
                            <p className="font-serif text-amber-900/80 italic">{item.description}</p>
                        </div>
                    )}

                    {/* Stats */}
                    <div>
                        <h3 className="text-sm font-serif font-semibold text-amber-800 mb-3">Stats</h3>
                        {stats.length > 0 ? (
                            <>
                                <div className="grid grid-cols-2 gap-2">
                                    {stats.map(([key, stat]) => (
                                        <div key={key} className="flex justify-between bg-white/40 px-3 py-2 rounded border border-amber-900/10">
                                            <span className="font-serif text-amber-900">{stat.label}</span>
                                            <span className="font-serif font-bold text-green-700">+{stat.value}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 flex justify-between px-3 py-2 rounded border-2 border-purple-700/30" style={{ background: 'linear-gradient(135deg, rgba(126, 34, 206, 0.1) 0%, rgba(126, 34, 206, 0.05) 100%)' }}>
                                    <span className="font-serif font-semibold text-purple-900">Total Bonus</span>
                                    <span className="font-serif font-bold text-purple-700">+{item.total_stat_bonus}</span>
                                </div>
                            </>
                        ) : item.recipe_fixed_stats ? (
                            <>
                                <p className="text-xs font-serif text-amber-800/60 mb-2">Recipe-defined stats:</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(item.recipe_fixed_stats).map(([key, stat]) => (
                                        <div key={key} className="flex justify-between bg-white/40 px-3 py-2 rounded border border-amber-900/10">
                                            <span className="font-serif text-amber-900">{stat.label}</span>
                                            <span className="font-serif font-bold text-green-700">+{stat.value}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 flex justify-between px-3 py-2 rounded border-2 border-purple-700/30" style={{ background: 'linear-gradient(135deg, rgba(126, 34, 206, 0.1) 0%, rgba(126, 34, 206, 0.05) 100%)' }}>
                                    <span className="font-serif font-semibold text-purple-900">Total Bonus</span>
                                    <span className="font-serif font-bold text-purple-700">
                                        +{Object.values(item.recipe_fixed_stats).reduce((sum, stat) => sum + stat.value, 0)}
                                    </span>
                                </div>
                            </>
                        ) : item.recipe_allowed_stats ? (
                            <div className="text-sm font-serif text-amber-800/70">
                                <p className="mb-2">Possible bonuses:</p>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(item.recipe_allowed_stats).map(([key, label]) => (
                                        <span key={key} className="px-2 py-1 bg-white/40 rounded text-amber-900 text-xs font-serif border border-amber-900/10">
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm font-serif text-amber-800/50 italic">No stats defined</p>
                        )}
                    </div>

                    {/* Character Assignment */}
                    {characters.length > 0 && (
                        <div className="border-t border-amber-900/20 pt-6">
                            <h3 className="text-sm font-serif font-semibold text-amber-800 mb-3">Assign to Character</h3>

                            {assignError && (
                                <div className="bg-red-800/10 border border-red-700/30 rounded-lg p-3 mb-3">
                                    <p className="font-serif text-red-800 text-sm">{assignError}</p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <select
                                    value={selectedCharacterId}
                                    onChange={(e) => {
                                        setSelectedCharacterId(e.target.value ? Number(e.target.value) : '');
                                        setAssignError(null);
                                    }}
                                    className="flex-1 bg-white/50 border border-amber-900/30 rounded px-3 py-2 font-serif text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-600/50"
                                    disabled={isAssigning}
                                >
                                    <option value="">Select a character...</option>
                                    {characters.map((char) => (
                                        <option key={char.id} value={char.id}>
                                            {char.name} ({char.class_label})
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={handleAssign}
                                    disabled={!selectedCharacterId || isAssigning}
                                    className="px-4 py-2 font-serif font-semibold rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(128, 90, 213, 0.9) 0%, rgba(107, 70, 193, 1) 100%)',
                                        color: '#fff',
                                    }}
                                >
                                    {isAssigning ? 'Assigning...' : 'Assign'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Unequip Button */}
                    {showUnequip && (
                        <div className="border-t border-amber-900/20 pt-6">
                            <h3 className="text-sm font-serif font-semibold text-amber-800 mb-3">Unequip Item</h3>
                            <p className="text-sm font-serif text-amber-800/60 mb-3">
                                Remove this item from the character and return it to your vault.
                            </p>
                            <button
                                type="button"
                                onClick={handleUnequip}
                                disabled={isUnequipping}
                                className="px-4 py-2 font-serif font-semibold rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                                style={{
                                    background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                                    color: '#fff',
                                }}
                            >
                                {isUnequipping ? 'Unequipping...' : 'Unequip Item'}
                            </button>
                        </div>
                    )}

                    {/* Recycle Button */}
                    {canRecycle && (
                        <div className="border-t border-amber-900/20 pt-6">
                            <h3 className="text-sm font-serif font-semibold text-amber-800 mb-3">Recycle Item</h3>

                            {recycleError && (
                                <div className="bg-red-800/10 border border-red-700/30 rounded-lg p-3 mb-3">
                                    <p className="font-serif text-red-800 text-sm">{recycleError}</p>
                                </div>
                            )}

                            {!showRecycleConfirm ? (
                                <>
                                    <p className="text-sm font-serif text-amber-800/60 mb-3">
                                        Destroy this item to receive random resources based on its rarity.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setShowRecycleConfirm(true)}
                                        className="px-4 py-2 font-serif font-semibold rounded transition-all hover:scale-105"
                                        style={{
                                            background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
                                            color: '#fff',
                                        }}
                                    >
                                        Recycle Item
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="bg-red-800/10 border border-red-700/30 rounded-lg p-4 mb-3">
                                        <p className="font-serif font-semibold text-red-800 mb-1">Are you sure?</p>
                                        <p className="text-sm font-serif text-red-700/80">
                                            This will permanently destroy "{item.name}" and cannot be undone.
                                            You will receive random resources based on the item's rarity.
                                        </p>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={handleRecycle}
                                            disabled={isRecycling}
                                            className="px-4 py-2 font-serif font-semibold rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                                            style={{
                                                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                                                color: '#fff',
                                            }}
                                        >
                                            {isRecycling ? 'Recycling...' : 'Yes, Recycle'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowRecycleConfirm(false)}
                                            disabled={isRecycling}
                                            className="px-4 py-2 font-serif text-amber-800 hover:text-amber-900 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Item Info */}
                    <div className="border-t border-amber-900/20 pt-4 text-xs font-serif text-amber-800/50">
                        {item.awarded_by && (
                            <p>Awarded by {item.awarded_by} on {item.awarded_at ? new Date(item.awarded_at).toLocaleDateString() : 'Unknown'}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ItemDetailModal;
